import { useEffect, useState } from 'react';
import { searchProfiles, getOrCreateConversation } from '@/lib/messages';
import type { ChatProfile } from '@/lib/messages';

interface NewConversationModalProps {
  open: boolean;
  currentUserId: string;
  onClose: () => void;
  onStarted: (conversationId: string) => void;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function NewConversationModal({
  open,
  currentUserId,
  onClose,
  onStarted,
}: NewConversationModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChatProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startingId, setStartingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setError('');
      setStartingId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const data = await searchProfiles(query, currentUserId);
        setResults(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open, currentUserId]);

  const handleStart = async (profile: ChatProfile) => {
    if (startingId) return;
    setStartingId(profile.id);
    try {
      const conv = await getOrCreateConversation(currentUserId, profile.id);
      onStarted(conv.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
    } finally {
      setStartingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-background-50 border border-background-200/70 rounded-xl w-full max-w-md shadow-xl shadow-black/20">
        <div className="flex items-center justify-between p-4 border-b border-background-200/70">
          <h3 className="font-heading font-semibold text-lg text-foreground-50">
            Nouvelle conversation
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-50 hover:bg-background-100 transition-colors"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-center bg-background-100 border border-background-300/60 rounded-lg px-3 mb-3">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pl-2 outline-none"
            />
          </div>

          {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-loader-4-line text-primary-400 animate-spin" />
                </div>
              </div>
            ) : query.trim().length < 2 ? (
              <p className="text-sm text-foreground-400 text-center py-8">
                Saisissez au moins 2 caractères pour rechercher un utilisateur.
              </p>
            ) : results.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-8">
                Aucun utilisateur trouvé.
              </p>
            ) : (
              <div className="space-y-1">
                {results.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleStart(p)}
                    disabled={startingId === p.id}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors text-left disabled:opacity-60"
                  >
                    {p.avatar_url ? (
                      <img
                        src={p.avatar_url}
                        alt={p.full_name || p.email || ''}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500/15 text-secondary-400 text-sm font-semibold">
                        {getInitials(p.full_name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-foreground-50 truncate">
                        {p.full_name || 'Utilisateur'}
                      </span>
                      <span className="block text-xs text-foreground-500 truncate">{p.email}</span>
                    </div>
                    <i className="ri-chat-3-line text-foreground-500" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewConversationModal;